import CryptoJS from "crypto-js";
import _axios from "axios";

import Utils from "@/utils/Utils";
import store from "@/store";
import router from "@/router";
import i18n from "../i18n";
import { debugLogRef } from "@/utils/debugLog";

function isTokenExpired(token) {
  const tokenPayload = Utils.getJWTPayload(token);
  const tokenExpiration = tokenPayload.exp;
  const now = Date.now() / 1000;
  const isExpired = tokenExpiration - now < 0;

  return isExpired;
}

const axios = _axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT,
});

const axiosWithoutInterceptor = _axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: import.meta.env.VITE_API_TIMEOUT,
});

function currentAccessToken() {
  return new Promise((res, rej) => {
    const accessToken = localStorage.getItem("iizenglish-access-token");

    if (accessToken !== null) {
      if (isTokenExpired(accessToken)) {
        const refreshToken = localStorage.getItem("iizenglish-refresh-token");

        if (isTokenExpired(refreshToken)) {
          store.dispatch("users/logout");
          if (import.meta.env.VITE_GAR == "true") {
            router.push({ name: "HomePage" });
          } else {
            router.push({ name: "AppLogin" });
          }
        } else {
          const payload = { refresh: refreshToken };
          axiosWithoutInterceptor
            .post("/access-token/refresh/", payload)
            .then((response) => {
              const newAccessToken = response.data.access;
              localStorage.setItem("iizenglish-access-token", newAccessToken);

              res(newAccessToken);
              return;
            });
        }
      } else {
        res(accessToken);
        return;
      }
    } else {
      rej("access_token doesn't exist");
      return;
    }
  });
}

axios.interceptors.request.use((config) => {
  // Also set session token if it exists
  const sessionToken = localStorage.getItem("session_token");
  if (sessionToken) {
    debugLogRef(
      "SESSION UNIQUE",
      "Repository interceptor: Ajout X-Session-Token header",
      sessionToken.substring(0, 8) + "...",
    );
    config.headers["X-Session-Token"] = sessionToken;
    store.dispatch("sessions/resetHeartbeat");
  }
  return currentAccessToken()
    .then((accessToken) => {
      // User is logged in. Set auth header on all requests
      config.headers.Authorization = "Bearer " + accessToken;
      return Promise.resolve(config);
    })
    .catch(() => {
      // No logged-in user: don't set auth header
      return Promise.resolve(config);
    });
});

axios.interceptors.response.use(
  (response) => {
    try {
      if (
        response.data &&
        response.headers["content-type"] !== "text/csv" &&
        "ciphertext" in response.data
      ) {
        const iv = import.meta.env.VITE_AES_IV;
        const key = import.meta.env.VITE_AES_SECRET_KEY;
        const ciphertext = response.data["ciphertext"];

        const keyWA = CryptoJS.enc.Utf8.parse(key);
        const ivWA = CryptoJS.enc.Utf8.parse(iv);

        let bytes = CryptoJS.AES.decrypt(ciphertext, keyWA, {
          iv: ivWA,
          mode: CryptoJS.mode.CBC,
        });
        let decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

        response.data = decryptedData;
      }
    } catch (error) {
      console.log("cryoptojs decryption error", error);
    }

    return Promise.resolve(response);
  },
  // Handle Errors
  (error) => {
    const accessToken = localStorage.getItem("iizenglish-access-token");

    // Détecter session invalide (409)
    if (error.response && error.response.status === 409) {
      debugLogRef(
        "SESSION UNIQUE",
        "Repository interceptor: Erreur 409 reçue",
        error.response.data,
      );
      store.commit("notifications/BLOCK_NOTIFICATIONS");
      store.dispatch("notifications/clean");
      const errorData = error.response.data || {};

      // Vérifier que c'est bien une erreur de session
      if (
        errorData.error?.toLowerCase().includes("session") ||
        errorData.detail?.toLowerCase().includes("session")
      ) {
        debugLogRef(
          "SESSION UNIQUE",
          "Repository interceptor: Session invalide détectée, redirection",
          { error: errorData.error, detail: errorData.detail },
        );
        // Session invalide détectée → rediriger immédiatement
        store.dispatch("sessions/handleInvalidSession", {
          source: "api",
        });
        return Promise.resolve({ data: null, _sessionInvalidated: true });
      }
    }
    // If we get a 401 response and there's no valid access token, the user session has been lost
    // Redirect to login page and show notification
    if (
      error.response &&
      error.response.status === 401 &&
      !accessToken &&
      router.currentRoute.value.meta.requiresAuth
    ) {
      router.push("/login");
      store.dispatch("notifications/addError", {
        message: i18n.global.t(`message.notifications.user.sessionLost`),
      });
    } else {
      // Any status codes that falls outside the range of 2xx cause this function to trigger
      return Promise.reject(error);
    }
  },
);

export default axios;
